import clsx from 'clsx'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/components-latest/primitives/Button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components-latest/primitives/Dialog'
import Separator from '@/components-latest/primitives/Separator'
import Text, { textVariants } from '@/components-latest/primitives/Text'

import Styles from './ConfirmationModal.module.scss'

interface IConfirmationModalProps {
  title: string
  description: string
  onCancel?: () => void
  onConfirm: () => Promise<unknown> | void
  children: React.ReactNode
  confirmButtonText?: string
}

const ConfirmationModal: React.FC<IConfirmationModalProps> = ({
  children,
  title,
  description,
  onCancel,
  onConfirm,
  confirmButtonText
}) => {
  const { t } = useTranslation()

  const [open, setOpen] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)

  const handleClose = () => setOpen(false)

  const handleCancel = () => {
    onCancel?.()
    handleClose()
  }
  const handleConfirm = async () => {
    setIsConfirming(true)
    try {
      // Only close once the action has settled: closing first made a failed
      // request look like a success.
      await onConfirm()
      handleClose()
      // The action owner surfaces the error; the modal just stays open.
      // eslint-disable-next-line no-empty
    } catch (error) {
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(state: boolean) => {
        if (isConfirming) return
        setOpen(state)
      }}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className={Styles.content}>
        <DialogHeader className={Styles.header}>
          <Text variant="nextSubtitle" weight="bold" align="center" asChild>
            <DialogTitle
              className={clsx(
                Styles.title,
                textVariants({ variant: 'nextSubtitle', weight: 'bold' })
              )}>
              {title}
            </DialogTitle>
          </Text>
          <DialogClose className={Styles.close} />
        </DialogHeader>
        <Separator />
        <DialogBody className={Styles.body}>
          <DialogDescription
            className={clsx(
              Styles.description,
              textVariants({ variant: 'nextSubtitle' })
            )}>
            {description}
          </DialogDescription>
        </DialogBody>

        <DialogFooter className={Styles.footer}>
          <Button
            size="medium"
            variant="tertiary"
            type="button"
            disabled={isConfirming}
            onClick={handleCancel}>
            {t('common.modal.cancel')}
          </Button>
          <Button
            size="medium"
            variant="primary"
            type="button"
            loading={isConfirming}
            disabled={isConfirming}
            onClick={handleConfirm}>
            {confirmButtonText || t('common.modal.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ConfirmationModal
